import type { CoreProfessional } from '@/lib/zifekCore';

interface Props {
  professionals: CoreProfessional[];
  orgNameById: Record<number, string>;
}

const VERIF_STYLES: Record<string, { label: string; className: string }> = {
  verified: { label: 'Vérifié', className: 'bg-accent-100 text-accent-900' },
  pending: { label: 'En attente', className: 'bg-secondary-100 text-secondary-900' },
  unverified: { label: 'Non vérifié', className: 'bg-foreground-100 text-foreground-600' },
};

export default function ProfessionalsPanel({ professionals, orgNameById }: Props) {
  if (professionals.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 bg-background-50 border border-background-200/70 rounded-lg">
        <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mb-3">
          <i className="ri-briefcase-4-line text-2xl text-foreground-400"></i>
        </div>
        <p className="text-foreground-600">Aucun professionnel.</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
      {professionals.map((p) => {
        const verif = VERIF_STYLES[p.verification_status] || VERIF_STYLES.unverified;
        return (
          <div key={p.id} className="bg-background-50 border border-background-200/70 rounded-lg p-5">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-lg bg-secondary-100 text-secondary-700 flex items-center justify-center flex-shrink-0">
                  <i className="ri-briefcase-4-line text-lg"></i>
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-foreground-950 truncate">{p.display_name || `Professionnel #${p.id}`}</div>
                  <div className="text-[11px] text-foreground-400 truncate">{p.profession || 'Profession non définie'}</div>
                </div>
              </div>
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${verif.className}`}>{verif.label}</span>
            </div>
            <div className="text-xs text-foreground-500 space-y-1">
              <div className="flex items-center gap-1.5"><i className="ri-building-2-line text-foreground-400"></i><span className="truncate">{p.organization_id ? (orgNameById[p.organization_id] || `Org #${p.organization_id}`) : 'Indépendant'}</span></div>
              <div className="flex items-center gap-1.5"><i className="ri-price-tag-3-line text-foreground-400"></i><span className="truncate">{p.specialties || '—'}</span></div>
              <div className="flex items-center gap-1.5"><i className="ri-translate-2 text-foreground-400"></i><span className="truncate">{p.languages || '—'}</span></div>
            </div>
            {p.public_profile && (
              <div className="mt-3 pt-3 border-t border-background-200/60">
                <span className="flex items-center gap-1.5 text-xs text-primary-700 bg-primary-50 rounded-full px-2.5 py-1 w-fit">
                  <i className="ri-eye-line"></i> Profil public
                </span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}